<template>
  <div v-rect-loading="loading" class="profile-page" :class="needCreateProfile && 'content-overlay'">
    <div class="head">
      <span class="label">{{ $t(`${i18nPrefix}.label.myProfile`) }}</span>
      <span v-if="!needCreateProfile" class="icon-group">
        <i class="edit-icon iconfont iconEditor" @click="handleEditResume"></i>
        <i v-rect-loading="exportPdfStatus.loading" class="download-icon iconfont icona-ResumeDownload" @click="handleDownloadProfile"></i>
      </span>
    </div>
    <div class="content">
      <div class="head-info">
        <el-avatar class="photo" :src="candidateProfile.base_info?.photo || '/static/images_en/default.jpg'"></el-avatar>
        <div class="profile-info">
          <div class="name">{{ candidateProfile.base_info?.legal_name }}</div>
          <el-tooltip v-if="candidateProfile.base_info?.personal_statement" effect="light" popper-class="light-tooltip-popper profile-popper" :content="candidateProfile.base_info?.personal_statement">
            <span class="description">{{ candidateProfile.base_info?.personal_statement }}</span>
          </el-tooltip>
        </div>
        <div class="view-btn">
          <el-button v-if="viewResume.hasFile" class="btn" @click="onViewResumeBtnClick">{{ $t(`${i18nPrefix}.btn.${viewResume.show ? 'viewProfile' : 'viewResume'}`)}}</el-button>
        </div>
      </div>
      <div v-show="!viewResume.show" class="content-container">
        <div class="profile-info">
          <BaseInfo
            v-if="candidateProfile.base_info"
            class="item-info-left"
            :baseInfo="candidateProfile.base_info"
          />
          <Education
            v-if="candidateProfile.education?.length > 0"
            class="item-info-left"
            :educations="candidateProfile.education"
          />
        </div>
        <div class="profile-info">
          <WorkExperience
            v-if="candidateProfile.experience?.part_time_experience?.length > 0"
            class="item-info-right"
            :dataList="candidateProfile.experience.part_time_experience"
            :isPartTime="true"
          />
          <WorkExperience
            v-if="candidateProfile.experience?.work_experience?.length > 0"
            class="item-info-right"
            :dataList="candidateProfile.experience.work_experience"
          />
          <AdditionalInfo
            v-if="candidateProfile.language"
            class="item-info-right"
            :languageList="candidateProfile.language"
          />
        </div>
      </div>
      <div v-show="viewResume.show" v-rect-loading="viewResume.loading" ref="viewer" class="resume-viewer">
        <PDFViewer v-if="viewResume.pdf" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL"/>
        <DocxViewer v-else-if="viewResume.docx" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL"/>
        <HTMLViewer v-else-if="viewResume.html" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL"/>
        <ImageViewer v-else-if="viewResume.image" class="viewer" ref="relViewer" :fileURL="viewResume.fileURL"/>
      </div>
    </div>
    <div v-if="needCreateProfile" class="overlay">
      <el-button class="create-btn" @click="handleCreateProfile">{{ $t(`${i18nPrefix}.btn.finishProfile`) }}</el-button>
    </div>
  </div>
</template>

<script>
import BaseInfo from '@/views/personalNew/components/pc/baseInfo.vue'
import Education from '@/views/personalNew/components/pc/education.vue'
import WorkExperience from '@/views/personalNew/components/pc/workExperience.vue'
import AdditionalInfo from '@/views/personalNew/components/pc/additionalInfo.vue'
import { mapActions } from 'vuex'
import { AsyncTask } from '@/utils/common'

export default {
  components: {
    BaseInfo,
    Education,
    WorkExperience,
    AdditionalInfo,
    PDFViewer: () => import('@/components/resumeViewer/pdf.vue'),
    DocxViewer: () => import('@/components/resumeViewer/docx.vue'),
    HTMLViewer: () => import('@/components/resumeViewer/html.vue'),
    ImageViewer: () => import('@/components/resumeViewer/image.vue')
  },
  props: {
    personalInfo: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      i18nPrefix: 'personal.profile',
      loading: false,
      candidateProfile: {},
      needCreateProfile: false,
      taskId: '',
      exportPdfStatus: {
        loading: false,
        task: null,
        finished: false
      },
      viewResume: {
        show: false,
        type: null,
        get pdf () { return this.type === 'pdf' },
        get docx () { return this.type === 'docx' },
        get html () { return this.type === 'html' },
        get image () { return this.type === 'image' },
        hasFile: false,
        fileURL: null,
        loading: false
      }
    }
  },
  computed: {
    companyId () {
      return this.$route.query.company_id
    },
    jobId () {
      return this.$route.query.job_id
    }
  },
  created () {
    this.initData()
  },
  mounted () {},
  methods: {
    ...mapActions('personal', ['getPersonalProfileInfo', 'exportPersonalProfilePDF', 'getExportPersonalProfilePDFTaskStatus']),
    async initData () {
      this.loading = true
      try {
        await this.loadCandidateProfileInfo()
        this.needCreateProfile = this.personalInfo.profile_step === 1 || this.personalInfo.need_to_finish_profile
      } finally {
        this.loading = false
      }
    },
    async loadCandidateProfileInfo () {
      const resp = await this.getPersonalProfileInfo({ company_id: this.companyId, job_id: this.jobId })
      this.candidateProfile = resp
      const baseInfo = resp.base_info
      if (['pdf', 'image', 'html', 'docx'].includes(baseInfo.resume_file_type)) {
        this.viewResume.hasFile = true
        this.viewResume.type = baseInfo.resume_file_type
        if (baseInfo.resume_file_url.startsWith('http')) {
          this.viewResume.fileURL = baseInfo.resume_file_url
        } else {
          if (process.env.NODE_ENV === 'production') {
            this.viewResume.fileURL = this.$DjangoMediaUrl(baseInfo.resume_file_url)
          } else {
            this.viewResume.fileURL = '/api/media/' + baseInfo.resume_file_url
          }
        }
      }
    },
    handleEditResume () {
      this.$router.push({ path: '/recruiting/personal/resume/edit', query: { ...this.$route.query, back: this.$route.fullPath } })
    },
    handleCreateProfile () {
      if (this.personalInfo.profile_step === 1) {
        this.$router.push({ path: '/recruiting/personal/resume/edit', query: { ...this.$route.query, back: this.$route.fullPath } })
      } else if (this.personalInfo.need_to_finish_profile) {
        this.$router.push({ path: '/recruiting/personal/resume/add', query: { ...this.$route.query, back: this.$route.fullPath } })
      }
    },
    onViewResumeBtnClick () {
      if (!this.viewResume.hasFile) {
        return
      }
      this.viewResume.show = !this.viewResume.show
      if (this.viewResume.show) {
        this.viewResume.loading = true
        this.$refs.relViewer.render().finally(() => {
          this.viewResume.loading = false
        })
      }
    },
    async handleDownloadProfile () {
      // 求职者端下载简历 下载的是系统生成的简历
      const exportPdfStatus = this.exportPdfStatus
      if (exportPdfStatus.loading) {
        return
      }
      exportPdfStatus.loading = true
      exportPdfStatus.finished = false
      exportPdfStatus.task = new AsyncTask(() => this.exportPersonalProfilePDF({
        show_report: false,
        show_base_info: true,
        company_id: this.companyId
      }), (taskId) => this.getExportPersonalProfilePDFTaskStatus({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/personal/export/profile/?task_id=${taskId}&download=1`)
        exportPdfStatus.loading = false
        exportPdfStatus.finished = false
        exportPdfStatus.task = null
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-page {
  .head {
    display: flex;
    position: sticky;
    top: -14px;
    height: 52px;
    padding: 16px 0 16px 40px;
    z-index: 3;
    background-color: #fff;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);

    .label {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      color: #606266;
    }

    .icon-group {
      flex: 1;
      text-align: right;
      margin-right: 24px;

      .edit-icon {
        margin-right: 28px;
        font-size: 20px;
        color: #0096cc;
        cursor: pointer;
      }

      .download-icon {
        font-size: 20px;
        color: #0096cc;
        cursor: pointer;

        ::v-deep {
          .el-loading-spinner {
            margin-top: -12px;

            .loading-spinner {
              .spinner {
                width: 20px;
                height: 20px;

                >div {
                  width: 3px;
                }
              }
            }
          }
        }
      }
    }
  }

  .content {
    background-color: #fff;

    .head-info {
      display: flex;
      padding: 24px;
      height: 138px;
      flex-direction: row;

      .photo {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 90px;
        width: 90px;
        flex-shrink: 0;
      }

      .profile-info {
        width: 595px;
        margin-left: 20px;

        .name {
          font-size: 16px;
          font-weight: 500;
          color: #242526;
          line-height: 22px;
        }

        .description {
          display: inline-block;
          margin-top: 8px;
          font-size: 14px;
          color: #606266;
          max-height: 60px;
          line-height: 20px;
          white-space: pre-wrap;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .view-btn {
        flex: 1;
        margin: auto 24px;
        text-align: right;

        .btn {
          width: 136px;
          border-color: #0096cc;
          color: #0096cc;
        }
      }
    }

    .content-container {
      margin-top: 24px;
      padding: 0 24px;
      display: flex;
      justify-content: space-between;

      .profile-info {
        flex: 1;

        .item-info-left {
          margin-right: 12px;
          margin-bottom: 24px;
        }

        .item-info-right {
          margin-left: 12px;
          margin-bottom: 24px;
        }
      }
    }

    .resume-viewer {
      margin: 24px;
      border: 1px solid #C0C4CC;
      position: relative;
      overflow: auto;
      height: 100%;

      .viewer {
        display: block;
        width: 100%;
        max-width: 800px;
        margin: auto;
      }

      .viewer-loading {
        position: absolute;
      }
    }
  }

}
.content-overlay {
  position: relative;
  max-height: calc(100vh - 28px - var(--header-height));
  max-height: calc(100dvh - 28px - var(--header-height));
  overflow: hidden;

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    z-index: 3;
    background-color: rgba(0, 0, 0, 0.5);

    .create-btn {
      background-color: #0096cc;
      color: #fff;
      border-radius: 4px;
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      border-color: #0096cc;
      transition: background-color 0.3s ease;

      &:hover {
        background-color: #0088b8;
      }
    }
  }
}

.profile-popper {
  white-space: pre-wrap;
}
</style>
